import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface TourVariant {
  id: string;
  tour_id: string;
  name: string | null;
  sku: string | null;
  option1_name: string | null;
  option1_value: string | null;
  option2_name: string | null;
  option2_value: string | null;
  option3_name: string | null;
  option3_value: string | null;
  price: number | null;
  compare_at_price: number | null;
  stock_quantity: number;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export interface TourVariantInput {
  tour_id: string;
  name?: string | null;
  sku?: string | null;
  option1_name?: string | null;
  option1_value?: string | null;
  option2_name?: string | null;
  option2_value?: string | null;
  option3_name?: string | null;
  option3_value?: string | null;
  price?: number | null;
  compare_at_price?: number | null;
  stock_quantity?: number;
  image?: string | null;
}

export function useTourVariants(tourId?: string) {
  const [variants, setVariants] = useState<TourVariant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVariants = useCallback(async () => {
    if (!tourId) return;
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('vietnamtravel_tour_variants')
        .select('*')
        .eq('tour_id', tourId)
        .order('created_at', { ascending: true });

      if (err) throw err;
      setVariants((data || []) as TourVariant[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load variants');
    } finally {
      setLoading(false);
    }
  }, [tourId]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  const createVariant = async (input: TourVariantInput) => {
    const { data, error: err } = await supabase
      .from('vietnamtravel_tour_variants')
      .insert(input)
      .select()
      .single();
    if (err) return { data: null, error: err.message };
    setVariants((prev) => [...prev, data as TourVariant]);
    return { data: data as TourVariant, error: null };
  };

  const updateVariant = async (id: string, updates: Partial<TourVariantInput>) => {
    const { data, error: err } = await supabase
      .from('vietnamtravel_tour_variants')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (err) return { data: null, error: err.message };
    setVariants((prev) => prev.map((v) => (v.id === id ? (data as TourVariant) : v)));
    return { data: data as TourVariant, error: null };
  };

  const deleteVariant = async (id: string) => {
    const { error: err } = await supabase.from('vietnamtravel_tour_variants').delete().eq('id', id);
    if (err) return { error: err.message };
    setVariants((prev) => prev.filter((v) => v.id !== id));
    return { error: null };
  };

  return { variants, loading, error, createVariant, updateVariant, deleteVariant, refetch: fetchVariants };
}